/**
 * JobsTab Component
 *
 * Displays paginated table of jobs for a batch with:
 * - Multi-select checkboxes for job selection
 * - "Select All" / "Deselect All" buttons
 * - "Run Selected" button to execute selected jobs
 * - Status filter dropdown
 * - Search input
 * - Table view with sortable columns
 * - Parse button on hover for raw/parse_failed jobs
 * - Pagination controls
 * - Real-time status updates with visual indicators
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBatchJobs } from '../../hooks/useBatchJobs';
import { useJobSelection } from '../../hooks/useJobSelection';
import { useExecutionProgress } from '../../hooks/useExecutionProgress';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { batchService, jobService } from '../../services/api';
import { useBatch } from '../../hooks/useBatches';
import { JobStatus } from '../../types/job';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface JobsTabProps {
  batchId: string;
}

const JOB_STATUS_OPTIONS: Array<{ value: '' | JobStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label:'Completed' },
  { value: 'run_failed', label: 'Run Failed' },
];

const EVAL_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'error', label: 'Error' },
  { value: 'n/a', label: 'N/A' },
];

export const JobsTab: React.FC<JobsTabProps> = ({ batchId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL or defaults
  const [statusFilter, setStatusFilter] = useState<'' | JobStatus>(searchParams.get('status') as any || '');
  const [evalFilter, setEvalFilter] = useState<string>(searchParams.get('eval') || '');
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [page, setPage] = useState<number>(parseInt(searchParams.get('page') || '0'));
  const limit = 50;

  // Update URL when filters change
  const updateFilters = (updates: Record<string, string | number>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Fetch batch to get groundtruth columns
  const { data: batch } = useBatch(batchId);
  const groundtruthColumns = batch?.groundtruth_columns || [];

  // Fetch jobs with filters (including server-side evaluation filter)
  const { data, isLoading, error, refetch } = useBatchJobs(batchId, {
    status: statusFilter || undefined,
    eval: evalFilter || undefined,
    search: searchQuery || undefined,
    limit,
    offset: page * limit,
  });

  // Job selection state
  const jobs = data?.jobs || [];

  const { selectedJobIds, selectAll, deselectAll, toggleJob, isSelected, selectedCount, hasSelection } = useJobSelection(jobs);

  // Real-time execution progress
  const { progress, isExecuting } = useExecutionProgress(batchId);

  // Get currently running job IDs from progress
  const runningJobIds = new Set(progress?.currently_executing_job_ids || []);

  // Groundtruth editing state
  const [isEditingGroundtruth, setIsEditingGroundtruth] = React.useState(false);
  const [editedGroundtruth, setEditedGroundtruth] = React.useState<Record<string, Record<string, any>>>({});

  // Cell-level editing state
  const [editingCell, setEditingCell] = React.useState<{jobId: string, column: string} | null>(null);
  const [cellEditValue, setCellEditValue] = React.useState('');

  // Range selection state
  const [rangeInput, setRangeInput] = React.useState('');
  const [showRangeSelector, setShowRangeSelector] = React.useState(false);

  // Helper to get the full output JSON (only from endpoint_result for now)
  const getOutputJSON = (job: any) => {
    // Only show endpoint_result for endpoint-based projects
    // For web agent jobs, output is too large to fetch for every job in table
    return job.endpoint_result || null;
  };

  // Save single cell edit
  const saveCellMutation = useMutation({
    mutationFn: async ({ jobId, column, value }: { jobId: string; column: string; value: string }) => {
      // Get current job using jobService
      const job = await jobService.get(jobId);

      // Update groundtruth and raw_input
      const updatedGroundtruth = { ...(job.groundtruth || {}), [column]: value };
      const updatedRawInput = { ...(job.raw_input || {}), [column]: value };

      // Update job using jobService
      return await jobService.update(jobId, {
        groundtruth: updatedGroundtruth,
        raw_input: updatedRawInput
      });
    },
    onSuccess: () => {
      toast.success('Value updated');
      setEditingCell(null);
      setCellEditValue('');
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update value');
    },
  });

  // Save edited groundtruth values (bulk)
  const saveGroundtruthMutation = useMutation({
    mutationFn: async () => {
      const updates = Object.entries(editedGroundtruth).map(async ([jobId, gtValues]) => {
        // Get the current job to update raw_input
        const jobResponse = await authenticatedFetch(`${API_BASE_URL}/api/jobs/${jobId}`);
        if (!jobResponse.ok) throw new Error(`Failed to fetch job ${jobId}`);
        const job = await jobResponse.json();

        // Update raw_input with the new groundtruth values
        const updatedRawInput = { ...(job.raw_input || {}) };
        Object.entries(gtValues).forEach(([key, value]) => {
          updatedRawInput[key] = value;
        });

        // Update both groundtruth and raw_input
        const response = await authenticatedFetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            groundtruth: gtValues,
            raw_input: updatedRawInput
          }),
        });
        if (!response.ok) throw new Error(`Failed to update job ${jobId}`);
        return response.json();
      });
      await Promise.all(updates);
      return Object.keys(editedGroundtruth).length;
    },
    onSuccess: (count) => {
      toast.success(`Updated ${count} groundtruth values`);
      setIsEditingGroundtruth(false);
      setEditedGroundtruth({});
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save groundtruth values');
    },
  });

  // Run selected jobs mutation
  const runSelectedMutation = useMutation({
    mutationFn: () => {
      if (!batchId) throw new Error('Batch ID is required');
      if (selectedJobIds.size === 0) throw new Error('No jobs selected');
      return batchService.startExecution(batchId, { job_ids: Array.from(selectedJobIds) });
    },
    onSuccess: (response) => {
      toast.success(`Execution started! ${response.jobs_queued} jobs queued.`);
      deselectAll(); // Clear selection after starting execution
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start execution');
    },
  });

  // Evaluate selected jobs mutation
  const evaluateSelectedMutation = useMutation({
    mutationFn: async () => {
      if (selectedJobIds.size === 0) throw new Error('No jobs selected');

      // Call evaluation endpoint for each selected job
      const jobIds = Array.from(selectedJobIds);
      const results = await Promise.allSettled(
        jobIds.map(async (jobId) => {
          const response = await authenticatedFetch(`${API_BASE_URL}/api/jobs/${jobId}/evaluate`, {
            method: 'POST',
          });
          if (!response.ok) throw new Error(`Failed to evaluate job ${jobId}`);
          return response.json();
        })
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { successful, failed, total: jobIds.length };
    },
    onSuccess: (result) => {
      toast.success(`Evaluation completed! ${result.successful} succeeded, ${result.failed} failed.`);
      deselectAll();
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to evaluate jobs');
    },
  });

  // Run jobs by status mutation
  const runByStatusMutation = useMutation({
    mutationFn: (status: string) => {
      if (!batchId) throw new Error('Batch ID is required');
      return batchService.executeByStatus(batchId, status);
    },
    onSuccess: (response) => {
      toast.success(`Execution started! ${response.jobs_queued} jobs queued.`);
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start execution');
    },
  });

  // Run jobs by evaluation status mutation
  const runByEvaluationMutation = useMutation({
    mutationFn: (evalStatus: string) => {
      if (!batchId) throw new Error('Batch ID is required');
      return batchService.executeByEvaluation(batchId, evalStatus);
    },
    onSuccess: (response) => {
      toast.success(`Execution started! ${response.jobs_queued} jobs queued.`);
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start execution');
    },
  });

  // Reset running jobs mutation
  const resetRunningMutation = useMutation({
    mutationFn: () => {
      if (!batchId) throw new Error('Batch ID is required');
      return jobService.resetRunning(batchId);
    },
    onSuccess: (response) => {
      toast.success(response.message);
      setShowResetConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reset running jobs');
      setShowResetConfirm(false);
    },
  });

  // Add job modal state
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [addJobForm, setAddJobForm] = useState({ goal: '', website_url: '', expected_result: '' });

  // Add job mutation
  const addJobMutation = useMutation({
    mutationFn: (data: { goal: string; website_url: string; input_id?: string; expected_result?: string }) => {
      return batchService.addJob(batchId, data);
    },
    onSuccess: () => {
      toast.success('Job added successfully');
      setShowAddJobModal(false);
      setAddJobForm({ goal: '', website_url: '', expected_result: '' });
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add job');
    },
  });

  // State for dropdowns and dialogs
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showEvalDropdown, setShowEvalDropdown] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [evalConfirm, setEvalConfirm] = useState<{ show: boolean; status: string; label: string }>({ show: false, status: '', label: '' });

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showStatusDropdown && !target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(false);
      }
      if (showEvalDropdown && !target.closest('.eval-dropdown-container')) {
        setShowEvalDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusDropdown, showEvalDropdown]);

  // Server-side filtering means data.total is already the filtered count
  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  // Helper function to truncate input_id
  const truncateInputId = (inputId: string | null | undefined, maxLength: number = 20): string => {
    if (!inputId) return '-';
    if (inputId.length <= maxLength) return inputId;
    return inputId.substring(0, maxLength) + '...';
  };

  // Parse range input and select jobs
  const handleRangeSelect = async () => {
    try {
      // Parse range input (e.g., "10-1000", "1,5,10-20,50")
      const ranges = rangeInput.split(',').map(s => s.trim());
      const rowIdsToSelect = new Set<number>();

      for (const range of ranges) {
        if (range.includes('-')) {
          // Handle range like "10-1000"
          const [start, end] = range.split('-').map(n => parseInt(n.trim()));
          if (isNaN(start) || isNaN(end)) continue;
          for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
            rowIdsToSelect.add(i);
          }
        } else {
          // Handle single number
          const num = parseInt(range);
          if (!isNaN(num)) rowIdsToSelect.add(num);
        }
      }

      // Fetch all jobs to find matching row_ids
      let allJobs: any[] = [];
      let offset = 0;
      const fetchLimit = 1000;

      while (true) {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/batches/${batchId}/jobs?limit=${fetchLimit}&offset=${offset}`);
        if (!response.ok) throw new Error('Failed to fetch jobs');
        const jobsData = await response.json();
        allJobs = allJobs.concat(jobsData.jobs);
        if (jobsData.jobs.length < fetchLimit) break;
        offset += fetchLimit;
      }

      // Select jobs with matching row_ids
      const jobIdsToSelect = allJobs
        .filter(job => rowIdsToSelect.has(job.row_id))
        .map(job => job.job_id);

      // Add to selection
      jobIdsToSelect.forEach(jobId => toggleJob(jobId));

      toast.success(`Selected ${jobIdsToSelect.length} jobs from range`);
      setShowRangeSelector(false);
      setRangeInput('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to select range');
    }
  };


  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading jobs</h3>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
            <div className="mt-4">
              <button
                onClick={() => refetch()}
                className="text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Status Filter
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                const newValue = e.target.value as '' | JobStatus;
                setStatusFilter(newValue);
                setPage(0);
                updateFilters({ status: newValue, page: 0 });
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {JOB_STATUS_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Evaluation Filter */}
          <div>
            <label htmlFor="eval-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Evaluation Filter
            </label>
            <select
              id="eval-filter"
              value={evalFilter}
              onChange={(e) => {
                const newValue = e.target.value;
                setEvalFilter(newValue);
                setPage(0);
                updateFilters({ eval: newValue, page: 0 });
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {EVAL_STATUS_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchQuery(newValue);
                setPage(0);
                updateFilters({ search: newValue, page: 0 });
              }}
              placeholder="Search by ID, URL, or goal..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Quick Actions - Run by Status */}
        {!isLoading && data && data.jobs.length > 0 && (
          <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
            <div className="relative status-dropdown-container">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={isExecuting || runByStatusMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                title="Execute jobs by status"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {runByStatusMutation.isPending ? 'Starting...' : 'Run by Status'}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showStatusDropdown && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <button
                      onClick={() => {
                        runByStatusMutation.mutate('pending');
                        setShowStatusDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
                        <span>Run All Pending</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Execute jobs not yet started</p>
                    </button>
                    <button
                      onClick={() => {
                        runByStatusMutation.mutate('run_failed');
                        setShowStatusDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-2 w-2 rounded-full bg-red-500"></span>
                        <span>Retry All Failed</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Re-execute failed jobs</p>
                    </button>
                    <button
                      onClick={() => {
                        runByStatusMutation.mutate('completed');
                        setShowStatusDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                        <span>Re-run All Completed</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Re-execute completed jobs</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Run by Evaluation Result Dropdown */}
            <div className="relative eval-dropdown-container">
              <button
                onClick={() => setShowEvalDropdown(!showEvalDropdown)}
                disabled={isExecuting || runByEvaluationMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                title="Execute jobs by evaluation result"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {runByEvaluationMutation.isPending ? 'Starting...' : 'Run by Eval'}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showEvalDropdown && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <button
                      onClick={() => {
                        setEvalConfirm({ show: true, status: 'fail', label: 'failed evaluation' });
                        setShowEvalDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-red-500">✗</span>
                        <span>Re-run All Failed Evals</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Re-execute jobs that failed evaluation</p>
                    </button>
                    <button
                      onClick={() => {
                        setEvalConfirm({ show: true, status: 'error', label: 'evaluation errors' });
                        setShowEvalDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-500">⚠</span>
                        <span>Re-run All Error Evals</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Re-execute jobs with eval errors</p>
                    </button>
                    <button
                      onClick={() => {
                        setEvalConfirm({ show: true, status: 'n/a', label: 'no evaluation result' });
                        setShowEvalDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">-</span>
                        <span>Run All N/A Evals</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Execute jobs not yet evaluated</p>
                    </button>
                    <button
                      onClick={() => {
                        setEvalConfirm({ show: true, status: 'pass', label: 'passed evaluation' });
                        setShowEvalDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-900"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        <span>Re-run All Passed Evals</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-4 mt-0.5">Re-execute jobs that passed evaluation</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowAddJobModal(true)}
              className="px-4 py-2 bg-white text-indigo-600 text-sm font-medium rounded-md border border-indigo-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 inline-flex items-center gap-2"
              title="Add a job entry manually"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Job
            </button>
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={resetRunningMutation.isPending}
              className="px-4 py-2 bg-white text-orange-600 text-sm font-medium rounded-md border border-orange-300 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              title="Reset stuck running jobs to pending status"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset Stuck Jobs
            </button>
            {isExecuting && (
              <span className="text-sm text-gray-600 italic">
                Execution in progress...
              </span>
            )}
          </div>
        )}

        {/* Reset Running Jobs Confirmation Dialog */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Reset Stuck Jobs?</h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  This will reset all jobs with status "running" back to "pending" in this batch.
                  This is useful for recovering from server restarts where jobs may be stuck.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    disabled={resetRunningMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => resetRunningMutation.mutate()}
                    disabled={resetRunningMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                  >
                    {resetRunningMutation.isPending ? 'Resetting...' : 'Reset Jobs'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Run by Evaluation Confirmation Dialog */}
        {evalConfirm.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Re-run Jobs by Evaluation?</h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  This will re-execute all jobs with {evalConfirm.label} in this batch.
                  The jobs will be queued for execution immediately.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setEvalConfirm({ show: false, status: '', label: '' })}
                    disabled={runByEvaluationMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      runByEvaluationMutation.mutate(evalConfirm.status);
                      setEvalConfirm({ show: false, status: '', label: '' });
                    }}
                    disabled={runByEvaluationMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {runByEvaluationMutation.isPending ? 'Starting...' : 'Re-run Jobs'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selection Actions */}
        {!isLoading && data && data.jobs.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-200">
            {!isEditingGroundtruth ? (
              <>
                <button
                  onClick={selectAll}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-label="Select all jobs"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAll}
                  disabled={!hasSelection}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Deselect all jobs"
                >
                  Deselect All
                </button>
                <button
                  onClick={() => setShowRangeSelector(!showRangeSelector)}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
                >
                  {showRangeSelector ? 'Hide Range' : 'Select Range'}
                </button>
                {showRangeSelector && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={rangeInput}
                      onChange={(e) => setRangeInput(e.target.value)}
                      placeholder="e.g., 10-1000 or 1,5,10-20"
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={handleRangeSelect}
                      disabled={!rangeInput.trim()}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      Apply
                    </button>
                  </div>
                )}
                {groundtruthColumns.length > 0 && (
                  <button
                    onClick={() => setIsEditingGroundtruth(true)}
                    className="px-3 py-1.5 text-sm font-medium text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50"
                  >
                    Edit Expected Values
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditingGroundtruth(false);
                    setEditedGroundtruth({});
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveGroundtruthMutation.mutate()}
                  disabled={Object.keys(editedGroundtruth).length === 0 || saveGroundtruthMutation.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {saveGroundtruthMutation.isPending ? 'Saving...' : `Save Changes (${Object.keys(editedGroundtruth).length})`}
                </button>
              </>
            )}
            {hasSelection && !isEditingGroundtruth && (
              <>
                <span className="text-sm text-gray-600 font-medium">
                  {selectedCount} job{selectedCount !== 1 ? 's' : ''} selected
                </span>
                <div className="ml-auto flex gap-2">
                  <button
                    onClick={() => evaluateSelectedMutation.mutate()}
                    disabled={evaluateSelectedMutation.isPending}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Re-run evaluation for selected jobs without re-executing them"
                    aria-label="Evaluate selected jobs"
                  >
                    {evaluateSelectedMutation.isPending ? 'Evaluating...' : 'Evaluate Selected'}
                  </button>
                  <button
                    onClick={() => runSelectedMutation.mutate()}
                    disabled={isExecuting || runSelectedMutation.isPending}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isExecuting ? 'Execution already in progress' : 'Execute selected jobs'}
                    aria-label="Run selected jobs"
                  >
                    {runSelectedMutation.isPending ? 'Starting...' : 'Run Selected'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Jobs Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : !data || jobs.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {statusFilter || evalFilter || searchQuery ? 'Try adjusting your filters' : 'No jobs in this batch yet'}
          </p>
          {!statusFilter && !evalFilter && !searchQuery && (
            <button
              onClick={() => setShowAddJobModal(true)}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Job
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Top Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 mb-4">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{page * limit + 1}</span> to{' '}
                  <span className="font-medium">{Math.min((page + 1) * limit, data.total)}</span> of{' '}
                  <span className="font-medium">{data.total}</span> results
                  {(statusFilter || evalFilter) && <span className="ml-1 text-gray-500">(filtered)</span>}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => {
                      const newPage = Math.max(0, page - 1);
                      setPage(newPage);
                      updateFilters({ page: newPage });
                    }}
                    disabled={page === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => {
                      const newPage = Math.min(totalPages - 1, page + 1);
                      setPage(newPage);
                      updateFilters({ page: newPage });
                    }}
                    disabled={page >= totalPages - 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedCount === jobs.length && jobs.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAll();
                          } else {
                            deselectAll();
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        aria-label="Select all jobs on this page"
                      />
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Input ID
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Goal
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Eval
                    </th>
                    {/* Groundtruth columns */}
                    {groundtruthColumns.map((column) => (
                      <th
                        key={column}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider bg-green-50 text-green-800"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Expected:</span>
                          {column}
                        </div>
                      </th>
                    ))}
                    <th scope="col" className="relative px-4 py-3 w-48">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobs.map((job) => {
                    const isRunning = runningJobIds.has(job.job_id);
                    const isJobSelected = isSelected(job.job_id);
                    return (
                      <tr
                        key={job.job_id}
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                          isRunning ? 'bg-yellow-50' : ''
                        }`}
                        onClick={(e) => {
                          // Don't navigate if clicking buttons or inputs
                          if ((e.target as HTMLElement).tagName === 'BUTTON' ||
                              (e.target as HTMLElement).tagName === 'INPUT' ||
                              (e.target as HTMLElement).closest('button') ||
                              (e.target as HTMLElement).closest('input')) {
                            return;
                          }
                          navigate(`/jobs/${job.job_id}`);
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isJobSelected}
                            onChange={() => toggleJob(job.job_id)}
                            className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                              isRunning ? 'animate-pulse' : ''
                            }`}
                            aria-label={`Select job ${job.input_id || job.job_id}`}
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" title={job.input_id || 'No input ID'}>
                          {truncateInputId(job.input_id)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 group relative">
                          <div className="max-w-[8rem] truncate">
                            {job.goal || <span className="text-gray-400 italic">Not parsed</span>}
                          </div>
                          {job.goal && (
                            <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 bottom-full left-0 mb-1 whitespace-normal max-w-md shadow-lg">
                              {job.goal}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : job.status === 'running'
                                ? 'bg-yellow-100 text-yellow-800'
                                : job.status === 'run_failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {isRunning && (
                              <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                            {job.status}
                          </span>
                        </td>
                        {/* Evaluation status - moved next to Status */}
                        <td className="px-4 py-4 whitespace-nowrap group relative">
                          {job.evaluation_status && job.evaluation_status !== 'n/a' ? (
                            <>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-help ${
                                  job.evaluation_status === 'pass'
                                    ? 'bg-green-100 text-green-800'
                                    : job.evaluation_status === 'fail'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {job.evaluation_status === 'pass' && '✓'}
                                {job.evaluation_status === 'fail' && '✗'}
                                {job.evaluation_status === 'error' && '⚠'}
                                {' '}{job.evaluation_status}
                              </span>
                              {job.evaluation_reason && (
                                <div className="invisible group-hover:visible absolute z-50 bg-gray-900 text-white text-xs rounded py-2 px-3 bottom-full left-0 mb-1 whitespace-normal max-w-md shadow-lg">
                                  {job.evaluation_reason}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        {/* Groundtruth values - inline editable */}
                        {groundtruthColumns.map((column) => {
                          const gtValue = editedGroundtruth[job.job_id]?.[column] ?? job.groundtruth?.[column] ?? '';
                          const isEditingThisCell = editingCell?.jobId === job.job_id && editingCell?.column === column;

                          return (
                            <td key={`gt-${column}`} className="px-4 py-4 text-sm bg-green-50 group">
                              {isEditingGroundtruth ? (
                                // Bulk edit mode
                                <input
                                  type="text"
                                  value={String(gtValue)}
                                  onChange={(e) => {
                                    setEditedGroundtruth(prev => ({
                                      ...prev,
                                      [job.job_id]: {
                                        ...(prev[job.job_id] || job.groundtruth || {}),
                                        [column]: e.target.value
                                      }
                                    }));
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : isEditingThisCell ? (
                                // Single cell edit mode
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={cellEditValue}
                                    onChange={(e) => setCellEditValue(e.target.value)}
                                    autoFocus
                                    className="w-full px-2 py-1 text-sm border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                                  />
                                  <button
                                    onClick={() => saveCellMutation.mutate({ jobId: job.job_id, column, value: cellEditValue })}
                                    disabled={saveCellMutation.isPending}
                                    className="p-1 text-green-600 hover:text-green-800"
                                    title="Save"
                                  >
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingCell(null);
                                      setCellEditValue('');
                                    }}
                                    className="p-1 text-gray-600 hover:text-gray-800"
                                    title="Cancel"
                                  >
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                // View mode with hover edit button
                                <div className="flex items-center justify-between gap-2">
                                  <div className="max-w-xs truncate flex-1">
                                    {String(gtValue || '-')}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCell({ jobId: job.job_id, column });
                                      setCellEditValue(String(gtValue || ''));
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-green-600 hover:text-green-800 transition-opacity"
                                    title="Edit"
                                  >
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {job.latest_session_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/sessions/${job.latest_session_id}`, '_blank');
                                }}
                                className="text-purple-600 hover:text-purple-900"
                                title="Open latest session in new tab"
                              >
                                Session
                              </button>
                            )}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await authenticatedFetch(`${API_BASE_URL}/api/jobs/${job.job_id}/run`, { method: 'POST' });
                                  toast.success('Job execution started');
                                  queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
                                } catch (error) {
                                  toast.error('Failed to run job');
                                }
                              }}
                              disabled={job.status === 'running'}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Run this job"
                            >
                              Run
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await authenticatedFetch(`${API_BASE_URL}/api/jobs/${job.job_id}/evaluate`, { method: 'POST' });
                                  toast.success('Evaluation completed');
                                  queryClient.invalidateQueries({ queryKey: ['batch-jobs', batchId] });
                                } catch (error) {
                                  toast.error('Failed to evaluate job');
                                }
                              }}
                              disabled={!job.status || job.status === 'pending'}
                              className="text-orange-600 hover:text-orange-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Re-evaluate this job"
                            >
                              Eval
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{page * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min((page + 1) * limit, data.total)}</span> of{' '}
                    <span className="font-medium">{data.total}</span> results
                    {(statusFilter || evalFilter) && <span className="ml-1 text-gray-500">(filtered)</span>}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => {
                      const newPage = Math.max(0, page - 1);
                      setPage(newPage);
                      updateFilters({ page: newPage });
                    }}
                      disabled={page === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      onClick={() => {
                      const newPage = Math.min(totalPages - 1, page + 1);
                      setPage(newPage);
                      updateFilters({ page: newPage });
                    }}
                      disabled={page >= totalPages - 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Job Modal */}
      {showAddJobModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Job</h3>
                <button
                  onClick={() => {
                    setShowAddJobModal(false);
                    setAddJobForm({ goal: '', website_url: '', expected_result: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="add-job-goal" className="block text-sm font-medium text-gray-700 mb-1">
                    Goal <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="add-job-goal"
                    value={addJobForm.goal}
                    onChange={(e) => setAddJobForm(prev => ({ ...prev, goal: e.target.value }))}
                    rows={3}
                    placeholder="e.g., Search for 'wireless headphones' and find the cheapest option"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="add-job-url" className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="add-job-url"
                    type="text"
                    value={addJobForm.website_url}
                    onChange={(e) => setAddJobForm(prev => ({ ...prev, website_url: e.target.value }))}
                    placeholder="e.g., https://www.amazon.com"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="add-job-expected" className="block text-sm font-medium text-gray-700 mb-1">
                    Expected Result <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    id="add-job-expected"
                    value={addJobForm.expected_result}
                    onChange={(e) => setAddJobForm(prev => ({ ...prev, expected_result: e.target.value }))}
                    rows={2}
                    placeholder="e.g., The cheapest option should be under $50"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowAddJobModal(false);
                    setAddJobForm({ goal: '', website_url: '', expected_result: '' });
                  }}
                  disabled={addJobMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const data: { goal: string; website_url: string; input_id: string; expected_result?: string } = {
                      goal: addJobForm.goal,
                      website_url: addJobForm.website_url,
                      input_id: crypto.randomUUID(),
                    };
                    if (addJobForm.expected_result.trim()) {
                      data.expected_result = addJobForm.expected_result.trim();
                    }
                    addJobMutation.mutate(data);
                  }}
                  disabled={addJobMutation.isPending || !addJobForm.goal.trim() || !addJobForm.website_url.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {addJobMutation.isPending && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {addJobMutation.isPending ? 'Adding...' : 'Add Job'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
